import { Component, h, State, Prop, Event, EventEmitter, Element } from '@stencil/core';

@Component({
  tag: 'visa-bubble-marquee',
  styleUrl: 'visa-bubble-marquee.scss',
  shadow: false,
})
export class VisaHero {
  @State() activeMode: 'personal' | 'business' = 'personal';
  @Prop() data;
  @Prop() accessibility;
  @State() screenWidth = 0;
  @Event() heroClicked: EventEmitter<void>;
  @Element() el: HTMLElement;
  private contentElement: HTMLElement;
  private observer: IntersectionObserver;

  private handleClick = () => {
    this.heroClicked.emit();
  }

  componentWillLoad() {
    this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    window.addEventListener('resize', this.debounce(this.resizeEventHandler, 300));
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.debounce(this.resizeEventHandler, 300));
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  componentDidLoad() {
    this.el.addEventListener('focusin', this.handleFocusIn);
    const options = {
      root: null,
      threshold: 0.5
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('vs-bubble-animate-up');
          const bubbleLinksController = this.el.querySelector('.vs-bubble-links-controller');
          if (bubbleLinksController) {
            bubbleLinksController.classList.add('vs-bubble-links-animate-up');
          }
          this.observer.unobserve(entry.target);
        }
      });
    }, options);

    this.contentElement = this.el.querySelector('.vs-bubble-content');
    if (this.contentElement) {
      this.observer.observe(this.contentElement);
    }
  }


  private handleFocusIn = () => {
    const event = new CustomEvent('marqueeFocusIn', {
      bubbles: true,
      composed: true,
    });
    this.el.dispatchEvent(event);
  };

  private setOverlayHeight = () => {
    if (this.screenWidth < 768) {
      const marqueeContainer = this.el.querySelector('.vs-bubblemarquee-container') as HTMLElement;
      const overlay = this.el.querySelector('.vs-bubble-overlay') as HTMLElement;

      if (marqueeContainer && overlay) {
        overlay.style.height = `${marqueeContainer.offsetHeight}px`;
      }
    }
  };

  resizeEventHandler = () => {
    this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
  };

  private debounce(func: Function, wait: number) {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  render() {
    var backgroundStyle = {}
    if (this.screenWidth < 768){
      backgroundStyle = {
        backgroundImage: `url(${this.data.mobileImagePath})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        role: 'img',
        'aria-label': this.data.mobileImageAltText || 'Background image for mobile view'
      };
    }
    else {
      backgroundStyle = {
        backgroundImage: `url(${this.data.desktopImagePath})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        role: 'img',
        'aria-label': this.data.desktopImageAltText || 'Background image for desktop view'
      };
    }

    return (
      <div class="vs-container-fluid vs-bubblemarquee-container" style={backgroundStyle} onClick={this.handleClick}>
        <img
          src={this.screenWidth < 768 ? this.data.mobileImagePath : this.data.desktopImagePath}
          alt={this.screenWidth < 768 ? this.data.mobileImageAltText : this.data.desktopImageAltText}
          style={{ width: '1px', height: '1px', opacity: '0', position: 'absolute' }}
        />
        {this.screenWidth >= 768 &&
          <div class={this.data.removeImageTransparency ? '' : 'vs-hero-marquee-overlay vs-bubble-overlay'}></div>
        }
        {this.screenWidth < 768 && <div class="vs-hero-marquee-overlay vs-bubble-overlay"></div>}
        <div>
          {this.data.embedPageToggle === true && (
            <div class="vs-bubble-toggle-container">
              <vs-page-toggle
                class="bubble-enabled"
                data={this.data.pageToggleProperties}
              ></vs-page-toggle>
            </div>)
          }
          <div class={`vs-row vs-bubble-marquee-content`} role="main">
            <div class="vs-bubble-content vs-col-md-6 vs-px-0">
              <div class={`vs-lead vs-mb-1 vs-pb-1 vs-bubble-eyebrow`} innerHTML={this.data.eyebrowText}></div>
              {this.data.headerStyle === 'h1' &&
                <h1 class={`vs-${this.data.headerStyle} vs-display-0 vs-mb-1 vs-pb-1 vs-bubble-header`} innerHTML={this.data.headerText}></h1>
              }
              {this.data.headerStyle === 'h2' &&
                <h2 class={`vs-${this.data.headerStyle} vs-display-0 vs-mb-1 vs-pb-1 vs-bubble-header`} innerHTML={this.data.headerText}></h2>
              }
              {this.data.headerStyle === 'h3' &&
                <h3 class={`vs-${this.data.headerStyle} vs-display-0 vs-mb-1 vs-pb-1 vs-bubble-header`} innerHTML={this.data.headerText}></h3>
              }
                 {this.data.headerStyle === 'h4' &&
                <h4 class={`vs-${this.data.headerStyle} vs-display-0 vs-mb-1 vs-pb-1 vs-bubble-header`} innerHTML={this.data.headerText}></h4>
              }
              <div class="vs-text vs-alt vs-mb-1 vs-pb-1 vs-bubble-body" innerHTML={this.data.bodyText}></div>
            </div>
            <div class="vs-col-md-6 vs-px-0">
              <bubble-links links={this.data.ctas} accessibility={this.accessibility} bubbleColor={this.data.bubbleColor}></bubble-links>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
